import useSWR from 'swr';
import { useParams } from 'react-router-dom';

import { getMyShops } from '@services/shop';

/**
 * Wraps the dashboard-shared `getMyShops` service with SWR and resolves the
 * active shop from the URL.
 *
 * Multi-shop orgs route under `/:shopId/...`, so the active shop is the
 * `:shopId` route param. Single-shop orgs keep the flat routes (no param) and
 * resolve to their only shop. An unknown/stale `:shopId` (not in the user's
 * org) falls back to the first shop; the `/:shopId` layout separately redirects
 * those so the URL self-heals.
 *
 * Returns `{ shop: null }` when the API resolves with no shops or errors —
 * consumers should render their loading / "no shop" state. There is no
 * frontend mock fallback; the dashboard expects a seeded shop in the
 * connected dev environment (see `docs/seed-audit.md`).
 */
export function useShop() {
	const { data, error, isLoading, mutate } = useSWR('unify:my-shops', getMyShops, {
		revalidateOnFocus: false,
		revalidateOnReconnect: true
	});
	const { shopId } = useParams();

	const shops = Array.isArray(data) ? data : [];
	const scoped = shopId ? shops.find((s) => s.id === shopId) : null;
	const shop = scoped ?? (shops.length > 0 ? shops[0] : null);

	return { shop, shops, error, isLoading, mutate };
}
